<template>
    <form-wrapper class="intake-hpi" @submit="handleFormSubmission(data)">
        <p v-if="!data.problems.length" class="intake-hpi__empty">
            {{ $t('forms.intake.symptom_details.empty') }}
        </p>

        <div v-for="(complaint, index) in data.problems" :key="`hpi-${index}`" class="intake-hpi__card">
            <h4 class="intake-hpi__card-title">{{ complaintTitle(complaint, index) }}</h4>

            <form-row>
                <form-cell>
                    <select-field
                        :label="$t('forms.intake.symptom_details.fields.began.label')"
                        :placeholder="$t('forms.intake.symptom_details.fields.began.placeholder')"
                        :options="beganOptions"
                        :formData="data"
                        :valuePath="`problems.${index}.began`"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <text-field
                        :label="$t('forms.intake.symptom_details.fields.severity.label')"
                        :formData="data"
                        :valuePath="`problems.${index}.severity`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <tabs-field
                        :label="$t('forms.intake.symptom_details.fields.course.label')"
                        :options="courseOptions"
                        :formData="data"
                        :valuePath="`problems.${index}.course`"
                        size="small"
                        @change="change($event)"
                    ></tabs-field>
                </form-cell>
            </form-row>

            <div class="intake-hpi__group">
                <label class="form-field__label">{{ $t('forms.intake.symptom_details.fields.timing.label') }}</label>
                <div class="intake-hpi__grid">
                    <checkbox-input
                        v-for="option in timingOptions"
                        :key="`timing-${index}-${option.value}`"
                        :label="option.label"
                        :value="(complaint.timing || []).includes(option.value)"
                        @change="toggleListValue(index, 'timing', option.value)"
                    ></checkbox-input>
                </div>
            </div>

            <div class="intake-hpi__group">
                <label class="form-field__label">{{ $t('forms.intake.symptom_details.fields.quality.label') }}</label>
                <div class="intake-hpi__grid">
                    <checkbox-input
                        v-for="option in qualityOptions"
                        :key="`quality-${index}-${option.value}`"
                        :label="option.label"
                        :value="(complaint.quality || []).includes(option.value)"
                        @change="toggleListValue(index, 'quality', option.value)"
                    ></checkbox-input>
                </div>
            </div>

            <div class="intake-hpi__group">
                <label class="form-field__label">{{ $t('forms.intake.symptom_details.fields.worse.label') }}</label>
                <div class="intake-hpi__grid">
                    <checkbox-input
                        v-for="option in worseOptions"
                        :key="`worse-${index}-${option.value}`"
                        :label="option.label"
                        :value="(complaint.worse || []).includes(option.value)"
                        @change="toggleListValue(index, 'worse', option.value)"
                    ></checkbox-input>
                </div>
            </div>

            <div class="intake-hpi__group">
                <label class="form-field__label">{{ $t('forms.intake.symptom_details.fields.better.label') }}</label>
                <div class="intake-hpi__grid">
                    <checkbox-input
                        v-for="option in betterOptions"
                        :key="`better-${index}-${option.value}`"
                        :label="option.label"
                        :value="(complaint.better || []).includes(option.value)"
                        @change="toggleListValue(index, 'better', option.value)"
                    ></checkbox-input>
                </div>
            </div>

            <div class="intake-hpi__group">
                <label class="form-field__label">
                    {{ $t('forms.intake.symptom_details.fields.radiation.label', { region: regionLabel(complaint) }) }}
                </label>
                <div class="intake-hpi__grid">
                    <checkbox-input
                        v-for="option in radiationOptions"
                        :key="`radiation-${index}-${option.value}`"
                        :label="option.label"
                        :value="(complaint.radiation || []).includes(option.value)"
                        @change="toggleListValue(index, 'radiation', option.value)"
                    ></checkbox-input>
                </div>
            </div>
        </div>

        <intake-form-section :title="$t('forms.intake.symptom_details.neurologic.title')">
            <p class="intake-hpi__prompt">{{ $t('forms.intake.symptom_details.neurologic.prompt') }}</p>
            <div class="intake-hpi__grid">
                <checkbox-input
                    v-for="option in neurologicOptions"
                    :key="`neuro-${option.value}`"
                    :label="option.label"
                    :value="(data.neurologicSymptoms || []).includes(option.value)"
                    @change="toggleNeurologic(option.value)"
                ></checkbox-input>
            </div>
        </intake-form-section>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('common.microcopy.next')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import IntakeFormSection from 'components/forms/intake/IntakeFormSection'

    const BEGAN_OPTIONS = ['immediately_after', 'same_day', 'next_day', 'within_several_days', 'later', 'unsure']
    const COURSE_OPTIONS = ['improving', 'unchanged', 'worsening', 'fluctuating']
    const TIMING_OPTIONS = ['all_the_time', 'morning', 'evening', 'night', 'occasionally']
    const QUALITY_OPTIONS = [
        'aching',
        'sharp',
        'burning',
        'throbbing',
        'pressure',
        'shooting',
        'cramping',
        'stabbing',
        'electric',
        'other',
    ]
    const WORSE_OPTIONS = [
        'sitting',
        'prolonged_sitting',
        'standing',
        'walking',
        'bending',
        'lifting',
        'twisting',
        'turning_head',
        'looking_up',
        'looking_down',
        'stairs',
        'driving',
        'work',
        'exercise',
        'activity',
        'stress',
        'lying_down',
        'coughing_sneezing',
        'other',
    ]
    const BETTER_OPTIONS = ['rest', 'medication', 'heat', 'ice', 'therapy', 'position_change', 'nothing', 'other']
    const NEUROLOGIC_OPTIONS = [
        'headaches',
        'dizziness',
        'fainting',
        'memory_problems',
        'difficulty_concentrating',
        'vision_changes',
        'balance_problems',
        'difficulty_walking',
        'arm_numbness',
        'arm_tingling',
        'arm_weakness',
        'hand_weakness',
        'dropping_objects',
        'leg_numbness',
        'leg_tingling',
        'leg_weakness',
        'foot_weakness',
    ]
    const RADIATION_OPTIONS = [
        'right_shoulder',
        'left_shoulder',
        'right_arm',
        'left_arm',
        'right_hand_fingers',
        'left_hand_fingers',
        'numbness',
        'tingling',
        'weakness',
    ]

    function normalizeComplaint(complaint = {}) {
        return {
            ...complaint,
            timing: Array.isArray(complaint.timing) ? complaint.timing : [],
            quality: Array.isArray(complaint.quality) ? complaint.quality : [],
            worse: Array.isArray(complaint.worse) ? complaint.worse : [],
            better: Array.isArray(complaint.better) ? complaint.better : [],
            radiation: Array.isArray(complaint.radiation) ? complaint.radiation : [],
        }
    }

    export default {
        mixins: [FormMixin],

        components: {
            IntakeFormSection,
        },

        props: {
            initialData: { type: Object, default: undefined },
        },

        data() {
            const problems = (this.initialData?.problems || []).map(normalizeComplaint)

            return {
                data: {
                    problems,
                    neurologicSymptoms: Array.isArray(this.initialData?.neurologicSymptoms)
                        ? this.initialData.neurologicSymptoms
                        : [],
                },
            }
        },

        computed: {
            beganOptions() {
                return BEGAN_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.symptom_details.fields.began.options.${value}`),
                    value,
                }))
            },

            courseOptions() {
                return COURSE_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.symptom_details.fields.course.options.${value}`),
                    value,
                }))
            },

            timingOptions() {
                return TIMING_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.symptom_details.fields.timing.options.${value}`),
                    value,
                }))
            },

            qualityOptions() {
                return QUALITY_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.symptom_details.fields.quality.options.${value}`),
                    value,
                }))
            },

            worseOptions() {
                return WORSE_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.symptom_details.fields.worse.options.${value}`),
                    value,
                }))
            },

            betterOptions() {
                return BETTER_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.symptom_details.fields.better.options.${value}`),
                    value,
                }))
            },

            radiationOptions() {
                return RADIATION_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.symptom_details.fields.radiation.options.${value}`),
                    value,
                }))
            },

            neurologicOptions() {
                return NEUROLOGIC_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.symptom_details.neurologic.options.${value}`),
                    value,
                }))
            },
        },

        methods: {
            complaintTitle(complaint, index) {
                const region = this.regionLabel(complaint)
                const type = (complaint.problemTypes || [])[0]
                const typeLabel = type
                    ? this.$t(`forms.intake.symptoms_today.fields.problem_types.options.${type}`)
                    : this.$t('forms.intake.review_submit.unnamed_problem', { number: index + 1 })

                return region ? `${region} — ${typeLabel}` : typeLabel
            },

            regionLabel(complaint) {
                if (!complaint.bodyRegion) {
                    return this.$t('forms.intake.symptom_details.fields.radiation.region_fallback')
                }

                return this.$t(`forms.intake.how_you_felt.fields.body_region.options.${complaint.bodyRegion}`)
            },

            toggleNeurologic(value) {
                const current = [...(this.data.neurologicSymptoms || [])]
                const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value]

                this.change({
                    path: 'neurologicSymptoms',
                    value: next,
                })
            },

            toggleListValue(index, path, value) {
                const complaint = this.data.problems[index] || {}
                const current = [...(complaint[path] || [])]
                const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value]

                this.change({
                    path: `problems.${index}.${path}`,
                    value: next,
                })
            },

            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';
    @import 'styles/setup/_mixins.scss';

    .intake-hpi__empty {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-hpi__prompt {
        margin: 0 0 rem(4px);
        font-size: rem($font-size--form);
        color: $color--txt-base;
    }

    .intake-hpi__card {
        margin-bottom: rem(16px);
        padding: rem(16px);
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: #fff;

        @include breakpoint(tablet) {
            padding: rem(12px);
        }
    }

    .intake-hpi__card-title {
        margin: 0 0 rem(16px);
        font-size: rem($font-size--form);
        font-weight: 600;
        color: $color--txt-header;
    }

    .intake-hpi__group {
        margin: rem(20px) 0 0;
    }

    .intake-hpi__grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: rem(12px) rem(16px);
        margin-top: rem(10px);

        @include breakpoint(tablet) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        @include breakpoint(smartphone) {
            grid-template-columns: 1fr;
        }
    }
</style>
